import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable, Subscription, combineLatest, of } from 'rxjs';
import { filter, shareReplay, switchMap, take } from 'rxjs/operators';
import {
  AddonService,
  AreaOfUseService,
  CatalogKeysService,
  CategoryService,
  PriceCampaignService,
} from '../../../../core/catalog';
import {
  AREA_OF_USE_ALL_CATEGORIES_CODE,
  AddonGroup,
  AddonNode,
  Category,
  Image,
  SapPriceCampaignDetails,
} from '../../../../core/model';

@Component({
  selector: 'py-catalog-header',
  templateUrl: './catalog-header.component.html',
  styleUrls: ['./catalog-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CatalogHeaderComponent implements OnInit, OnDestroy {
  category$: Observable<Category>;
  defaultPriceCampaignDetails$: Observable<SapPriceCampaignDetails>;
  addonNode$ = new BehaviorSubject<AddonNode>(undefined);
  addonGroup$ = new BehaviorSubject<AddonGroup>(undefined);
  bannerImage: Image;

  @Input()
  set addonNode(addonNode: AddonNode | undefined) {
    this.addonNode$.next(addonNode);
  }

  @Input()
  set addonGroup(addonGroup: AddonGroup | undefined) {
    this.addonGroup$.next(addonGroup);
  }

  @Input() hasPriceCampaign?: boolean;
  @Input() numberOfSelectedCampaigns?: number;
  @Input() priceCampaignDetails?: SapPriceCampaignDetails;

  get addon(): AddonGroup | AddonNode {
    return !!this.addonNode$.getValue() ? this.addonNode$.getValue() : this.addonGroup$.getValue();
  }

  subscription = new Subscription();

  constructor(
    private categoryService: CategoryService,
    private addonService: AddonService,
    private catalogKeysService: CatalogKeysService,
    private areaOfUseService: AreaOfUseService,
    protected activatedRoute: ActivatedRoute,
    private priceCampaignService: PriceCampaignService
  ) {}

  ngOnInit(): void {
    this.category$ = combineLatest([this.activatedRoute.data, this.activatedRoute.queryParamMap]).pipe(
      switchMap(([data, params]) => {
        if (!!data.categoryCode) {
          return this.catalogKeysService
            .getCatalogUsageKeyForParams(params.get('mya'))
            .pipe(switchMap((catalogUsageKey) => this.categoryService.getCategory(data.categoryCode, catalogUsageKey)));
        } else if (params.get('aou') && params.get('aou') !== AREA_OF_USE_ALL_CATEGORIES_CODE) {
          return this.areaOfUseService.getAreaOfUse(params.get('aou'));
        }
        return of(undefined);
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.subscription.add(
      combineLatest([this.category$, this.activatedRoute.data, this.activatedRoute.queryParamMap])
        .pipe(filter(([_category, data, params]) => !!params.get('aou') || !!data.categoryCode))
        .subscribe(([category, data, params]) => {
          this.bannerImage = null;
          if (!!category) {
            if (!!data.categoryCode) {
              this.getCategoryBanner(category);
            } else if (params.get('aou') && params.get('aou') !== AREA_OF_USE_ALL_CATEGORIES_CODE) {
              this.getAreaOfUseBanner(category);
            }
          }
        })
    );

    this.subscription.add(
      combineLatest([this.addonGroup$, this.addonNode$])
        .pipe(filter(([addonGroup]) => !!addonGroup))
        .subscribe(([addonGroup, addonNode]) => {
          if (addonNode) {
            this.getAddonImage(addonNode);
          } else {
            this.getAddonImage(addonGroup);
          }
        })
    );

    this.defaultPriceCampaignDetails$ = this.priceCampaignService
      .getDefaultPriceCampaignDetails()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private getCategoryBanner(category: Category) {
    if (category.image || category.banner) {
      this.bannerImage = category.image || category.banner;
    } else if (category.superCategoryRef) {
      this.subscription.add(
        this.catalogKeysService
          .getCatalogUsageKey()
          .pipe(
            take(1),
            switchMap((catalogUsageKey) => this.categoryService.getCategory(category.superCategoryRef, catalogUsageKey))
          )
          .subscribe((c) => {
            this.getCategoryBanner(c);
          })
      );
    }
  }

  private getAreaOfUseBanner(areaOfUse: Category) {
    if (areaOfUse.image || areaOfUse.banner) {
      this.bannerImage = areaOfUse.image || areaOfUse.banner;
    } else if (areaOfUse.superCategoryRef) {
      this.subscription.add(
        this.areaOfUseService
          .getAreaOfUse(areaOfUse.superCategoryRef)
          .pipe(take(1))
          .subscribe((c) => {
            this.getAreaOfUseBanner(c);
          })
      );
    }
  }

  private getAddonImage(addon: AddonGroup | AddonNode) {
    if (addon.image) {
      this.bannerImage = addon.image;
    } else if (addon.superGroupId) {
      this.subscription.add(
        this.addonService
          .getAddonGroup(addon.superGroupId)
          .pipe(take(1))
          .subscribe((a) => {
            this.getAddonImage(a);
          })
      );
    }
  }
}
